import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	PageContext,
	PageContextAPI,
	FETCH_PAGE_CONTEXT_URL,
	pageContextDefaultState,
	PAGE_CONTEXT_CACHE_TIMEOUT,
} from './const';
import useFetch from '../../hooks/useFetch';
import typeUtils from '../../utils/type';
import { CacheManager } from '../../utils/cacheManager';
import { getComputedPersistKey } from './utils';
import stringUtils from '../../utils/string';
import { storageManager, LocalStorageKeys } from '../../utils/storageManager';

const PageContextProvider = ({ children }) => {
	const cacheManagerRef = useRef(null);
	const { fetch, response, isPending } = useFetch(FETCH_PAGE_CONTEXT_URL);
	const pageTypeIdRef = useRef(null);
	const fetchPendingRef = useRef(isPending);
	const shouldFetch = useCallback((pageTypeIdParam) => {
		const pageTypeId = pageTypeIdParam || pageTypeIdRef.current;
		if (pageTypeId === null || fetchPendingRef.current === true) {
			return false;
		}

		if (cacheManagerRef.current !== null && cacheManagerRef.current.hasInvalidateCacheTime()) {
			return true;
		}

		return storageManager.getSubItem(LocalStorageKeys.PageContext, pageTypeId) === null;
	}, []);
	const [pageContext, setPageContext] = useState(pageContextDefaultState);
	const fetchContext = useCallback(
		(pageTypeId) => {
			if (stringUtils.isNullOrEmpty(pageTypeId)) {
				throw new Error('Provide pageTypeId for PageContextProvider');
			}

			const persistedPageContext = storageManager.getSubItem(LocalStorageKeys.PageContext, pageTypeId);
			const normalizedPersistedPageContext = persistedPageContext === null ? {} : persistedPageContext;
			pageTypeIdRef.current = pageTypeId;
			setPageContext((v) => ({
				...v,
				...normalizedPersistedPageContext,
			}));

			return fetch({
				pageTypeId,
			});
		},
		[fetch],
	);
	const tryFetch = useCallback(
		(pageTypeId) => {
			cacheManagerRef.current = new CacheManager(getComputedPersistKey(pageTypeId), PAGE_CONTEXT_CACHE_TIMEOUT);
			if (shouldFetch(pageTypeId)) {
				fetchContext(pageTypeId);
			}
		},
		[shouldFetch, fetchContext],
	);

	useEffect(() => {
		fetchPendingRef.current = isPending;
	}, [isPending]);

	useEffect(() => {
		const responseData = response.data;

		if (typeUtils.isNullOrUndefined(responseData) || pageTypeIdRef.current === null) {
			return;
		}

		storageManager.setSubItem(LocalStorageKeys.PageContext, pageTypeIdRef.current, responseData);
		cacheManagerRef.current.resetStartDate();

		setPageContext((prevValue) => ({
			...prevValue,
			...responseData,
			isInitialized: true,
			isPending: false,
		}));
	}, [response]);

	const setPageTypeId = useCallback((pageTypeId) => {
		setPageContext((prevValue) => ({
			...prevValue,
			pageTypeId,
		}));
	}, []);

	const pageContextApi = useMemo(
		() => ({
			setPageTypeId,
			fetchPageContext: fetchContext,
			tryFetchPageContext: tryFetch,
			resetPageContextCache: () => {
				if (pageTypeIdRef.current === null) {
					return;
				}

				storageManager.removeSubItem(LocalStorageKeys.PageContext, pageTypeIdRef.current);
				cacheManagerRef.current.resetStartDate();
			},
		}),
		[setPageTypeId, fetchContext, tryFetch],
	);

	return (
		<PageContext.Provider value={pageContext}>
			<PageContextAPI.Provider value={pageContextApi}>{children}</PageContextAPI.Provider>
		</PageContext.Provider>
	);
};

export { PageContextProvider };
